import { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuoteForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    property_type: '',
    size: '',
    frequency: '',
    services: [],
    message: '',
    honeypot: '', // Spam protection
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const serviceOptions = [
    'Regular Cleaning',
    'Deep Clean',
    'End of Lease',
    'Office Cleaning',
    'Carpet Cleaning',
    'Window Cleaning',
  ];

  const handleServiceToggle = (service) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter((s) => s !== service)
        : [...prev.services, service],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Basic validation
    if (!formData.name || !formData.email || !formData.phone || !formData.property_type || !formData.size || !formData.frequency || formData.services.length === 0) {
      setError('Please fill in all required fields');
      setIsSubmitting(false);
      return;
    }

    try {
      await axios.post(`${API}/quotes`, formData);
      setIsSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        property_type: '',
        size: '',
        frequency: '',
        services: [],
        message: '',
        honeypot: '',
      });

      // Reset success message after 5 seconds
      setTimeout(() => setIsSuccess(false), 5000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit quote request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div data-testid="quote-success" className="text-center py-12 animate-fade-in">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-success/10 rounded-full mb-4">
          <CheckCircle2 className="w-8 h-8 text-success" />
        </div>
        <h3 className="text-2xl font-semibold text-primary mb-2">Thank You!</h3>
        <p className="text-muted-foreground max-w-md mx-auto">
          Your quote request has been submitted successfully. We'll get back to you within 24 hours.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} data-testid="quote-form" className="space-y-6 max-w-3xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Name */}
        <div className="space-y-2">
          <Label htmlFor="name">Name *</Label>
          <Input
            id="name"
            data-testid="quote-name"
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            className="h-12 rounded-lg"
          />
        </div>

        {/* Email */}
        <div className="space-y-2">
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            data-testid="quote-email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
            className="h-12 rounded-lg"
          />
        </div>

        {/* Phone */}
        <div className="space-y-2">
          <Label htmlFor="phone">Phone *</Label>
          <Input
            id="phone"
            data-testid="quote-phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            required
            className="h-12 rounded-lg"
          />
        </div>

        {/* Property Type */}
        <div className="space-y-2">
          <Label htmlFor="property_type">Property Type *</Label>
          <Select
            value={formData.property_type}
            onValueChange={(value) => setFormData({ ...formData, property_type: value })}
          >
            <SelectTrigger data-testid="quote-property-type" className="h-12 rounded-lg">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="House">House</SelectItem>
              <SelectItem value="Apartment">Apartment</SelectItem>
              <SelectItem value="Office">Office</SelectItem>
              <SelectItem value="Retail">Retail</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Size */}
        <div className="space-y-2">
          <Label htmlFor="size">Approx. Size *</Label>
          <Select
            value={formData.size}
            onValueChange={(value) => setFormData({ ...formData, size: value })}
          >
            <SelectTrigger data-testid="quote-size" className="h-12 rounded-lg">
              <SelectValue placeholder="Select size" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="<50 sqm">&lt;50 sqm</SelectItem>
              <SelectItem value="50-100 sqm">50-100 sqm</SelectItem>
              <SelectItem value="100-200 sqm">100-200 sqm</SelectItem>
              <SelectItem value="200-500 sqm">200-500 sqm</SelectItem>
              <SelectItem value="500+ sqm">500+ sqm</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Frequency */}
        <div className="space-y-2">
          <Label htmlFor="frequency">Cleaning Frequency *</Label>
          <Select
            value={formData.frequency}
            onValueChange={(value) => setFormData({ ...formData, frequency: value })}
          >
            <SelectTrigger data-testid="quote-frequency" className="h-12 rounded-lg">
              <SelectValue placeholder="Select frequency" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="One-time">One-time</SelectItem>
              <SelectItem value="Weekly">Weekly</SelectItem>
              <SelectItem value="Fortnightly">Fortnightly</SelectItem>
              <SelectItem value="Monthly">Monthly</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Services */}
      <div className="space-y-3">
        <Label>Services Needed *</Label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {serviceOptions.map((service) => (
            <div key={service} className="flex items-center space-x-2">
              <Checkbox
                id={service}
                data-testid={`service-${service.toLowerCase().replace(/\s+/g, '-')}`}
                checked={formData.services.includes(service)}
                onCheckedChange={() => handleServiceToggle(service)}
              />
              <Label htmlFor={service} className="cursor-pointer font-normal">
                {service}
              </Label>
            </div>
          ))}
        </div>
      </div>

      {/* Message */}
      <div className="space-y-2">
        <Label htmlFor="message">Additional Message (Optional)</Label>
        <Textarea
          id="message"
          data-testid="quote-message"
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
          rows={4}
          className="rounded-lg"
        />
      </div>

      {/* Honeypot (hidden) */}
      <input
        type="text"
        name="website"
        value={formData.honeypot}
        onChange={(e) => setFormData({ ...formData, honeypot: e.target.value })}
        style={{ display: 'none' }}
        tabIndex="-1"
        autoComplete="off"
      />

      {error && (
        <div data-testid="quote-error" className="bg-destructive/10 text-destructive px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      <Button
        type="submit"
        data-testid="quote-submit"
        disabled={isSubmitting}
        className="w-full md:w-auto rounded-full bg-primary hover:bg-primary/90 text-white px-12 py-6 text-lg font-medium"
      >
        {isSubmitting ? 'Submitting...' : 'Request a Free Quote'}
      </Button>
    </form>
  );
};

export default QuoteForm;
